import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import type { RequestContext } from "../context/request-context.js";
import type { 
  IntegrityReportRepository,
  InspectionRepository,
  RecommendationRepository,
  ChecklistRepository
} from "../../repositories/operational-repositories.js";
import type { ChecklistComponent, ChecklistPhoto } from "../../domain/inspection/checklist-domain.js";
import { type IntegrityReport, canDeleteReport } from "../../domain/report/integrity-report-domain.js";
import { normalizeEngineeringAssessment, type EngineeringAssessment } from "../../domain/report/integrity-assessment.js";
import { isPlhQualifiedProfessional } from "../../domain/identity/plh-eligibility.js";
import { CLIENT_WITHOUT_PLH, ProjectResponsibilityService } from "./project-responsibility-service.js";
import { PipingSystemLineListService } from "./piping-system-line-list-service.js";
import type { GeneratedLineList } from "../../domain/inspection-book/line-list.js";
import { 
  type Recommendation,
  validateRecommendationCreation
} from "../../domain/recommendation/recommendation-domain.js";
import type { DocumentNumberAllocator } from "./document-numbering-service.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { AuditAction } from "../../audit/audit-log.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
const randomUUID = () => globalThis.crypto.randomUUID();
import { domainId, type InspectionId, type IntegrityReportId, type EquipmentId, type NdtReportId, type ClientId, type MeasurementReportId, type ProfessionalId, type RecommendationId, type ChecklistId, type ChecklistComponentId } from "../../shared/kernel/identifiers.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { UnifiedInspectionIntegrityReportMapper } from "../mappers/unified-inspection-integrity-report-mapper.js";
import { notificationService } from "./notification-service.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import {
  appendZrHistory,
  assertZrNotDuplicated,
  isPendingZr,
  normalizeZrNumber,
  requireZrNumber,
  requireZrReason,
  ZR_ALREADY_PENDING_MESSAGE,
  ZR_REMOVAL_REASON_REQUIRED_MESSAGE
} from "../../domain/recommendation/zr-numbering.js";

export class ReportService {
  constructor(
    public reportRepo: IntegrityReportRepository,
    public inspectionRepo?: InspectionRepository,
    public recommendationRepo?: RecommendationRepository,
    public checklistRepo?: ChecklistRepository,
    public audit?: AuditRecorder,
    public equipmentService?: any,
    public identityRepo?: any,
    public instrumentService?: any,
    public safetyDeviceService?: any,
    public projectRepo?: any,
    public ndtServices?: {
      meReportRepo?: any;
      lpReportRepo?: any;
      pmReportRepo?: any;
      uscReportRepo?: any;
      bscanReportRepo?: any;
      meReportService?: any;
      lpReportService?: any;
      pmReportService?: any;
      conventionalUsReportService?: any;
      bscanReportService?: any;
    }
  ) {}

  public attachNdtServices(services: NonNullable<ReportService['ndtServices']>): void {
    this.ndtServices = { ...(this.ndtServices || {}), ...services };
  }

  /**
   * Resolvedor central dos responsáveis (Engenheiro pelo Projeto, PLH pelo
   * Cliente do Projeto). Montado sob demanda com os mesmos repositórios já
   * injetados — a regra do PLH nunca é reescrita aqui.
   */
  private responsibilityResolver(): ProjectResponsibilityService | null {
    if (!this.projectRepo) return null;
    if (!this.responsibilityCache) {
      this.responsibilityCache = new ProjectResponsibilityService(this.projectRepo, this.identityRepo);
    }
    return this.responsibilityCache;
  }
  private responsibilityCache: ProjectResponsibilityService | null = null;

  /**
   * O PLH do RI vem do CLIENTE do Projeto (Client.responsiblePlhProfessionalId)
   * — fonte de verdade única. Depois de enviado ao PLH a revisão preserva o
   * snapshot congelado no envio (responsibility) e não segue mudanças
   * posteriores do cadastro.
   */
  private async syncEngineerFromProject(report: IntegrityReport, context: RequestContext): Promise<IntegrityReport> {
    if (report.status === 'SIGNED' || report.status === 'COMPLETED') return report;
    if (!this.projectRepo || !this.inspectionRepo) return report;
    if (report.responsibility && report.status !== 'DRAFT' && report.status !== 'IN_ELABORATION' && report.status !== 'FINALIZED_BY_INSPECTOR' && report.status !== 'RETURNED' && report.status !== 'IN_CORRECTION') {
      return report;
    }

    try {
      const inspection = await this.inspectionRepo.findById(context, report.inspectionId);
      if (inspection && (inspection as any).projectId) {
        const project = await this.projectRepo.getProject((inspection as any).projectId);
        const resolver = this.responsibilityResolver();
        if (project && resolver) {
          // Id bruto do PLH do Cliente: a habilitação é conferida depois por
          // assertPlhQualified, que reporta exatamente o requisito faltante.
          const resolved = await resolver.resolveClientResponsiblePlh(project.clientId);
          report.engineerPlhId = (resolved.professionalId || null) as any;
        }
      }
    } catch {}

    return report;
  }

  /**
   * Congela Engenheiro (do Projeto) e PLH (do Cliente do Projeto) no RI —
   * chamado no envio ao PLH. Depois disso, trocar o PLH do Cliente não
   * altera este documento.
   */
  private async freezeResponsibility(report: IntegrityReport, context: RequestContext): Promise<void> {
    if (!this.projectRepo || !this.inspectionRepo) return;
    try {
      const inspection = await this.inspectionRepo.findById(context, report.inspectionId);
      const projectId = (inspection as any)?.projectId;
      const resolver = this.responsibilityResolver();
      if (!projectId || !resolver) return;
      const snapshot = await resolver.snapshotOf(context, String(projectId), String(report.clientId));
      if (snapshot) report.responsibility = snapshot;
    } catch { /* sem projeto: nada a congelar */ }
  }

  async getReportById(context: RequestContext, reportId: IntegrityReportId): Promise<IntegrityReport | null> {
    const report = await this.reportRepo.findById(context, reportId);
    if (report) {
      return this.syncEngineerFromProject(report, context);
    }
    return report;
  }

  async getReportByInspection(context: RequestContext, inspectionId: InspectionId): Promise<IntegrityReport | null> {
    const report = await this.reportRepo.findByInspection(context, inspectionId);
    if (report) {
      return this.syncEngineerFromProject(report, context);
    }
    return report;
  }

  async listReports(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly IntegrityReport[]> {
    if (!context || !context.activeClientId) {
      return [];
    }
    return this.reportRepo.listAll(context, options);
  }

  async listRecommendations(context: RequestContext, reportId: IntegrityReportId): Promise<readonly Recommendation[]> {
    return this.listRecommendationsByReport(context, reportId);
  }

  async listAllRecommendations(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly Recommendation[]> {
    if (!this.recommendationRepo) return [];
    return this.recommendationRepo.listAll(context, options);
  }

  async buildDocumentSnapshot(context: RequestContext, reportId: IntegrityReportId): Promise<DocumentSnapshot> {
    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Integrity Report not found');
    report = await this.syncEngineerFromProject(report, context);

    const scopedContext: RequestContext = {
      ...context,
      activeClientId: (report.clientId || context.activeClientId) as any
    };

    let inspection: any = null;
    if (this.inspectionRepo) {
      try {
        inspection = await this.inspectionRepo.findById(scopedContext, report.inspectionId);
      } catch {
        // Continue with null inspection if not found
      }
    }

    let projectData: any = null;
    const pId = inspection?.projectId || (report as any).projectId;
    if (this.projectRepo && pId) {
      try {
        projectData = await this.projectRepo.getProject(pId);
      } catch {}
    }

    let equipmentSnapshot: any = null;
    let targetEquipmentId = inspection?.equipmentId;

    if (report.equipmentSnapshotId && this.equipmentService) {
      try {
        equipmentSnapshot = await this.equipmentService.getSnapshot(scopedContext, report.equipmentSnapshotId);
        if (!targetEquipmentId && equipmentSnapshot) {
          targetEquipmentId = equipmentSnapshot.equipmentId || equipmentSnapshot.equipmentData?.id;
        }
      } catch {
        // Fall back to querying master equipment
      }
    }

    // Always synchronize with the latest master equipment data to ensure modified equipment fields and new attributes are reflected in old reports and PDFs, UNLESS report is historical
    const isHistorical = report.status === 'SIGNED' || report.status === 'COMPLETED';
    if (!isHistorical && targetEquipmentId && this.equipmentService) {
      try {
        const masterEq = await this.equipmentService.getEquipment(scopedContext, targetEquipmentId);
        if (masterEq) {
          const prevData = equipmentSnapshot?.equipmentData || equipmentSnapshot || {};
          const mergedProperties = {
            ...(prevData.properties || {}),
            ...(masterEq.properties || {})
          };
          const mergedData = {
            ...prevData,
            ...masterEq,
            properties: mergedProperties
          };
          equipmentSnapshot = {
            ...(equipmentSnapshot || {}),
            equipmentId: targetEquipmentId,
            equipmentData: mergedData,
            clientName: masterEq.clientName || equipmentSnapshot?.clientName,
            projectName: masterEq.projectName || equipmentSnapshot?.projectName,
            unitAreaName: masterEq.unitAreaName || equipmentSnapshot?.unitAreaName
          };
        }
      } catch {
        // Continue with available snapshot
      }
    } else if (!equipmentSnapshot && inspection?.equipmentId && this.equipmentService) {
      try {
        const masterEq = await this.equipmentService.getEquipment(scopedContext, inspection.equipmentId);
        if (masterEq) {
          equipmentSnapshot = { equipmentData: masterEq };
        }
      } catch {
        // Equipment snapshot remains null if not retrievable
      }
    }

    let safetyDevices: any[] = [];
    let instruments: any[] = [];

    // Fetch safety devices and instruments if we have equipment context
    if (!targetEquipmentId && equipmentSnapshot && equipmentSnapshot.equipmentData) {
       targetEquipmentId = equipmentSnapshot.equipmentData.id;
    }

    if (targetEquipmentId) {
      if (this.safetyDeviceService) {
        try {
          safetyDevices = await this.safetyDeviceService.listSafetyDevices(scopedContext, { protectedEquipmentId: targetEquipmentId });
        } catch {}
      }
      if (this.instrumentService) {
        try {
          instruments = await this.instrumentService.listInstruments(scopedContext, { equipmentId: targetEquipmentId });
        } catch {}
      }
    }

    let checklist: any = null;
    const checklistComponents: ChecklistComponent[] = [];
    const photos: ChecklistPhoto[] = [];

    if (this.checklistRepo && report.inspectionId) {
      try {
        checklist = await this.checklistRepo.findByInspection(scopedContext, report.inspectionId);
      } catch (err: any) {
        throw new AppError(ErrorCode.Persistence, `Não foi possível carregar o Checklist vinculado à inspeção ${report.inspectionId}: ${err?.message || err}`);
      }

      if (checklist) {
        try {
          const comps = await this.checklistRepo.getComponents(scopedContext, checklist.id);
          for (const comp of comps) {
            checklistComponents.push(comp);
            if (this.checklistRepo.getPhotos) {
              const compPhotos = await this.checklistRepo.getPhotos(scopedContext, comp.id);
              photos.push(...compPhotos);
            }
            if ((comp as any).photos && Array.isArray((comp as any).photos)) {
              for (const pUrl of (comp as any).photos) {
                if (typeof pUrl === 'string') {
                  photos.push({
                    id: `ph-${comp.id}-${photos.length + 1}` as any,
                    clientId: (report.clientId || context.activeClientId) as any,
                    componentId: comp.id,
                    fileUrl: pUrl,
                    comment: comp.definitionComponentId || 'Evidência Fotográfica',
                    order: photos.length + 1,
                    createdBy: context.userId,
                    createdAt: new Date().toISOString()
                  });
                }
              }
            }
          }
        } catch (err: any) {
          throw new AppError(ErrorCode.Persistence, `Não foi possível carregar os componentes do Checklist ${checklist.id}: ${err?.message || err}`);
        }
      }
    }

    // Inspection-level local persistence fallback for offline or in-memory preview
    if (report.inspectionId && typeof localStorage !== 'undefined') {
      try {
        const storedInspChk = localStorage.getItem(`ri_checklist_insp_${report.inspectionId}`);
        if (storedInspChk) {
          const parsed = JSON.parse(storedInspChk);
          if (Array.isArray(parsed) && parsed.length > 0) {
            if (checklistComponents.length === 0) {
              for (const item of parsed) {
                const cId = item.id || `comp-${report.inspectionId}-${item.index || checklistComponents.length + 1}`;
                checklistComponents.push({
                  id: cId as any,
                  clientId: (report.clientId || context.activeClientId) as any,
                  checklistId: `chk-${report.inspectionId}` as any,
                  groupId: item.name || 'Componente',
                  name: item.name || 'Componente',
                  definitionComponentId: item.name || 'Componente',
                  result: item.status === 'APPROVED' ? 'APPROVED' : (item.status === 'REJECTED' ? 'REJECTED' : (item.status === 'NOT_APPLICABLE' ? 'NOT_APPLICABLE' : (item.status === 'CLIENT_DISCRETION' ? 'CLIENT_DISCRETION' : 'PENDING'))),
                  description: item.condition || (item.status === 'APPROVED' ? 'Sem anomalias (Aprovado)' : (item.status === 'REJECTED' ? (item.technicalComment || 'Anomalia registrada') : null)),
                  inspectionNotes: item.technicalComment || null,
                  photos: item.photos || [],
                  recommendations: item.recommendations || [],
                  user: context.userId,
                  timestamp: new Date().toISOString()
                } as any);

                if (item.photos && Array.isArray(item.photos)) {
                  for (const pUrl of item.photos) {
                    photos.push({
                      id: `ph-${cId}-${photos.length + 1}` as any,
                      clientId: (report.clientId || context.activeClientId) as any,
                      componentId: cId as any,
                      fileUrl: pUrl,
                      comment: item.name || 'Evidência Fotográfica',
                      order: photos.length + 1,
                      createdBy: context.userId,
                      createdAt: new Date().toISOString()
                    });
                  }
                }
              }
            } else {
              // Merge cached recommendations and names onto repo components
              for (const comp of checklistComponents) {
                const compName = (comp as any).name || (comp as any).groupId || (comp as any).definitionComponentId;
                const matchCache = parsed.find((p: any) => p.id === comp.id || p.name === compName);
                if (matchCache) {
                  if (!(comp as any).name) (comp as any).name = matchCache.name;
                  if (matchCache.recommendations && matchCache.recommendations.length > 0) {
                    if (!(comp as any).recommendations || (comp as any).recommendations.length === 0) {
                      (comp as any).recommendations = matchCache.recommendations || [];
                    } else {
                      for (const cachedRec of matchCache.recommendations) {
                        const existingRec = (comp as any).recommendations.find((r: any) => r.id === cachedRec.id || r.code === cachedRec.code);
                        if (existingRec) {
                          if (cachedRec.description) existingRec.description = cachedRec.description;
                          if (cachedRec.scope) existingRec.scope = cachedRec.scope;
                          if (cachedRec.zr) existingRec.zr = cachedRec.zr;
                        } else {
                          (comp as any).recommendations.push(cachedRec);
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      } catch {
        // Safe fallback
      }
    }

    let recommendations: readonly Recommendation[] = [];
    if (this.recommendationRepo) {
      try {
        const byReport = await this.recommendationRepo.findByIntegrityReport(scopedContext, reportId);
        if (byReport && byReport.length > 0) {
          recommendations = byReport;
        } else if (report.inspectionId && this.recommendationRepo.findByInspection) {
          const byInsp = await this.recommendationRepo.findByInspection(scopedContext, report.inspectionId);
          if (byInsp && byInsp.length > 0) {
            recommendations = byInsp;
          }
        }
      } catch {
        // Safe fallback
      }
    }

    // Ensure components have matching recommendations attached
    if (recommendations && recommendations.length > 0) {
      for (const comp of checklistComponents) {
        const cName = ((comp as any).name || comp.groupId || comp.definitionComponentId || '').toLowerCase().trim();
        const cId = (comp.id || '').toLowerCase();
        const matched = recommendations.filter(r => {
          if (r.checklistItemId && (r.checklistItemId === comp.id || (r.checklistItemId as string).toLowerCase() === cId)) return true;
          if (r.componentId && (r.componentId === comp.id || (r.componentId as string).toLowerCase() === cId)) return true;
          if (r.componentName && r.componentName.toLowerCase().trim() === cName) return true;
          if (r.scope && cName && (r.scope.toLowerCase().startsWith(cName + ':') || r.scope.toLowerCase().startsWith(cName + ' -'))) return true;
          return false;
        });
        if (matched.length > 0) {
          if ((comp as any).recommendations && (comp as any).recommendations.length > 0) {
            for (const existing of (comp as any).recommendations) {
              const matchReal = matched.find(m => m.id === existing.id || (m.code && m.code === existing.code) || (m.recommendationNumber && m.recommendationNumber === existing.code));
              if (matchReal) {
                if (existing.zr !== (matchReal.zr || undefined)) {
                  existing.zr = matchReal.zr || undefined;
                }
                if (matchReal.scope) {
                  const cleanedScope = cName && matchReal.scope.toLowerCase().startsWith(cName + ':')
                    ? matchReal.scope.slice(cName.length + 1).trim()
                    : matchReal.scope;
                  existing.description = cleanedScope;
                }
              }
            }
          } else {
            (comp as any).recommendations = matched.map(m => ({
              id: m.id,
              code: m.code || m.recommendationNumber,
              description: m.scope,
              criticality: m.criticality,
              discipline: m.discipline,
              execution: m.executeIn === 'SHUTDOWN' ? 'PARADA' : 'ROTINA',
              zr: m.zr || undefined
            }));
          }
        }
      }
    }

    // Performed Inspection Methods & NDTs - authoritative backend gathering
    const performedInspectionMethods: any[] = [];
    if (Array.isArray((report as any).performedInspectionMethods) && (report as any).performedInspectionMethods.length > 0) {
      performedInspectionMethods.push(...(report as any).performedInspectionMethods);
    } else if (Array.isArray((report as any).ndts) && (report as any).ndts.length > 0) {
      performedInspectionMethods.push(...(report as any).ndts);
    }

    if (report.inspectionId) {
      const inspId = report.inspectionId;
      const ndts = this.ndtServices || {};

      // 1. ME Report
      if (ndts.meReportRepo?.findByInspection) {
        try {
          const me = await ndts.meReportRepo.findByInspection(scopedContext, inspId);
          if (me && !performedInspectionMethods.some(p => p.id === me.id || p.method === 'ME')) {
            performedInspectionMethods.push({
              id: me.id,
              method: 'ME',
              methodName: 'MEDIÇÃO DE ESPESSURA (ME)',
              reportNumber: me.reportNumber || me.clientReportNumber || me.id,
              date: me.testDate ? new Date(me.testDate).toLocaleDateString('pt-BR') : (me.createdAt ? new Date(me.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: me.status === 'FINALIZED' || me.status === 'SIGNED' ? 'APROVADO' : (me.status || 'EM ELABORAÇÃO'),
              resultDisplay: me.status === 'FINALIZED' || me.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (me as any).notes || (me as any).conclusion || '-'
            });
          }
        } catch {}
      } else if (ndts.meReportService?.getByInspectionId) {
        try {
          const me = await ndts.meReportService.getByInspectionId(scopedContext, inspId);
          if (me && !performedInspectionMethods.some(p => p.id === me.id || p.method === 'ME')) {
            performedInspectionMethods.push({
              id: me.id,
              method: 'ME',
              methodName: 'MEDIÇÃO DE ESPESSURA (ME)',
              reportNumber: me.reportNumber || me.clientReportNumber || me.id,
              date: me.testDate ? new Date(me.testDate).toLocaleDateString('pt-BR') : (me.createdAt ? new Date(me.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: me.status === 'FINALIZED' || me.status === 'SIGNED' ? 'APROVADO' : (me.status || 'EM ELABORAÇÃO'),
              resultDisplay: me.status === 'FINALIZED' || me.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (me as any).notes || (me as any).conclusion || '-'
            });
          }
        } catch {}
      }

      // 2. LP Report
      if (ndts.lpReportRepo?.findByInspection) {
        try {
          const lp = await ndts.lpReportRepo.findByInspection(scopedContext, inspId);
          if (lp && !performedInspectionMethods.some(p => p.id === lp.id || p.method === 'LP')) {
            performedInspectionMethods.push({
              id: lp.id,
              method: 'LP',
              methodName: 'LÍQUIDO PENETRANTE (LP)',
              reportNumber: lp.reportNumber || lp.clientReportNumber || lp.id,
              date: lp.testDate ? new Date(lp.testDate).toLocaleDateString('pt-BR') : (lp.createdAt ? new Date(lp.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: lp.status === 'FINALIZED' || lp.status === 'SIGNED' ? 'APROVADO' : (lp.status || 'EM ELABORAÇÃO'),
              resultDisplay: lp.status === 'FINALIZED' || lp.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (lp as any).notes || (lp as any).conclusion || '-'
            });
          }
        } catch {}
      } else if (ndts.lpReportService?.getByInspectionId) {
        try {
          const lp = await ndts.lpReportService.getByInspectionId(scopedContext, inspId);
          if (lp && !performedInspectionMethods.some(p => p.id === lp.id || p.method === 'LP')) {
            performedInspectionMethods.push({
              id: lp.id,
              method: 'LP',
              methodName: 'LÍQUIDO PENETRANTE (LP)',
              reportNumber: lp.reportNumber || lp.clientReportNumber || lp.id,
              date: lp.testDate ? new Date(lp.testDate).toLocaleDateString('pt-BR') : (lp.createdAt ? new Date(lp.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: lp.status === 'FINALIZED' || lp.status === 'SIGNED' ? 'APROVADO' : (lp.status || 'EM ELABORAÇÃO'),
              resultDisplay: lp.status === 'FINALIZED' || lp.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (lp as any).notes || (lp as any).conclusion || '-'
            });
          }
        } catch {}
      }

      // 3. PM Report
      if (ndts.pmReportRepo?.findByInspection) {
        try {
          const pm = await ndts.pmReportRepo.findByInspection(scopedContext, inspId);
          if (pm && !performedInspectionMethods.some(p => p.id === pm.id || p.method === 'PM')) {
            performedInspectionMethods.push({
              id: pm.id,
              method: 'PM',
              methodName: 'PARTÍCULAS MAGNÉTICAS (PM)',
              reportNumber: pm.reportNumber || pm.clientReportNumber || pm.id,
              date: pm.testDate ? new Date(pm.testDate).toLocaleDateString('pt-BR') : (pm.createdAt ? new Date(pm.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: pm.status === 'FINALIZED' || pm.status === 'SIGNED' ? 'APROVADO' : (pm.status || 'EM ELABORAÇÃO'),
              resultDisplay: pm.status === 'FINALIZED' || pm.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (pm as any).notes || (pm as any).conclusion || '-'
            });
          }
        } catch {}
      } else if (ndts.pmReportService?.getByInspectionId) {
        try {
          const pm = await ndts.pmReportService.getByInspectionId(scopedContext, inspId);
          if (pm && !performedInspectionMethods.some(p => p.id === pm.id || p.method === 'PM')) {
            performedInspectionMethods.push({
              id: pm.id,
              method: 'PM',
              methodName: 'PARTÍCULAS MAGNÉTICAS (PM)',
              reportNumber: pm.reportNumber || pm.clientReportNumber || pm.id,
              date: pm.testDate ? new Date(pm.testDate).toLocaleDateString('pt-BR') : (pm.createdAt ? new Date(pm.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: pm.status === 'FINALIZED' || pm.status === 'SIGNED' ? 'APROVADO' : (pm.status || 'EM ELABORAÇÃO'),
              resultDisplay: pm.status === 'FINALIZED' || pm.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (pm as any).notes || (pm as any).conclusion || '-'
            });
          }
        } catch {}
      }

      // 4. USC Report
      if (ndts.uscReportRepo?.findByInspection) {
        try {
          const usc = await ndts.uscReportRepo.findByInspection(scopedContext, inspId);
          if (usc && !performedInspectionMethods.some(p => p.id === usc.id || p.method === 'USC')) {
            performedInspectionMethods.push({
              id: usc.id,
              method: 'USC',
              methodName: 'ULTRASSOM (US)',
              reportNumber: usc.reportNumber || usc.clientReportNumber || usc.id,
              date: usc.testDate ? new Date(usc.testDate).toLocaleDateString('pt-BR') : (usc.createdAt ? new Date(usc.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: usc.status === 'FINALIZED' || usc.status === 'SIGNED' ? 'APROVADO' : (usc.status || 'EM ELABORAÇÃO'),
              resultDisplay: usc.status === 'FINALIZED' || usc.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (usc as any).notes || (usc as any).conclusion || '-'
            });
          }
        } catch {}
      } else if (ndts.conventionalUsReportService?.getByInspectionId) {
        try {
          const usc = await ndts.conventionalUsReportService.getByInspectionId(scopedContext, inspId);
          if (usc && !performedInspectionMethods.some(p => p.id === usc.id || p.method === 'USC')) {
            performedInspectionMethods.push({
              id: usc.id,
              method: 'USC',
              methodName: 'ULTRASSOM (US)',
              reportNumber: usc.reportNumber || usc.clientReportNumber || usc.id,
              date: usc.testDate ? new Date(usc.testDate).toLocaleDateString('pt-BR') : (usc.createdAt ? new Date(usc.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: usc.status === 'FINALIZED' || usc.status === 'SIGNED' ? 'APROVADO' : (usc.status || 'EM ELABORAÇÃO'),
              resultDisplay: usc.status === 'FINALIZED' || usc.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (usc as any).notes || (usc as any).conclusion || '-'
            });
          }
        } catch {}
      }

      // 5. BSCAN Report
      if (ndts.bscanReportRepo?.findByInspection) {
        try {
          const bscan = await ndts.bscanReportRepo.findByInspection(scopedContext, inspId);
          if (bscan && !performedInspectionMethods.some(p => p.id === bscan.id || p.method === 'BSCAN')) {
            performedInspectionMethods.push({
              id: bscan.id,
              method: 'BSCAN',
              methodName: 'B-SCAN',
              reportNumber: bscan.reportNumber || bscan.clientReportNumber || bscan.id,
              date: bscan.testDate ? new Date(bscan.testDate).toLocaleDateString('pt-BR') : (bscan.createdAt ? new Date(bscan.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: bscan.status === 'FINALIZED' || bscan.status === 'SIGNED' ? 'APROVADO' : (bscan.status || 'EM ELABORAÇÃO'),
              resultDisplay: bscan.status === 'FINALIZED' || bscan.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (bscan as any).notes || (bscan as any).conclusion || '-'
            });
          }
        } catch {}
      } else if (ndts.bscanReportService?.getByInspectionId) {
        try {
          const bscan = await ndts.bscanReportService.getByInspectionId(scopedContext, inspId);
          if (bscan && !performedInspectionMethods.some(p => p.id === bscan.id || p.method === 'BSCAN')) {
            performedInspectionMethods.push({
              id: bscan.id,
              method: 'BSCAN',
              methodName: 'B-SCAN',
              reportNumber: bscan.reportNumber || bscan.clientReportNumber || bscan.id,
              date: bscan.testDate ? new Date(bscan.testDate).toLocaleDateString('pt-BR') : (bscan.createdAt ? new Date(bscan.createdAt).toLocaleDateString('pt-BR') : '-'),
              result: bscan.status === 'FINALIZED' || bscan.status === 'SIGNED' ? 'APROVADO' : (bscan.status || 'EM ELABORAÇÃO'),
              resultDisplay: bscan.status === 'FINALIZED' || bscan.status === 'SIGNED' ? 'APROVADO' : 'EM ELABORAÇÃO',
              notes: (bscan as any).notes || (bscan as any).conclusion || '-'
            });
          }
        } catch {}
      }

      // Offline localStorage fallback only if in browser and array is still empty
      if (performedInspectionMethods.length === 0 && typeof localStorage !== 'undefined') {
        try {
          const storedTests = localStorage.getItem(`ri_performed_tests_${report.inspectionId}`);
          if (storedTests) {
            const parsed = JSON.parse(storedTests);
            if (parsed && Array.isArray(parsed.performedTests)) {
              for (let idx = 0; idx < parsed.performedTests.length; idx++) {
                const t = parsed.performedTests[idx];
                if (typeof t === 'string') {
                  performedInspectionMethods.push({
                    id: `pm-${idx + 1}`,
                    method: t,
                    methodName: t,
                    reportNumber: '-',
                    date: '-',
                    result: 'APPROVED',
                    notes: '-'
                  });
                } else if (t) {
                  performedInspectionMethods.push({
                    id: `pm-${idx + 1}`,
                    method: t.methodId,
                    methodName: t.methodId === 'OTHER' ? (t.otherName || 'Outros') : t.methodId,
                    reportNumber: '-',
                    date: '-',
                    result: t.result || 'APPROVED',
                    notes: t.comment || '-'
                  });
                }
              }
            }
          }
        } catch {}
      }
    }

    // Structured Reference Documents
    let clientNameFromRepo = null;
    if (this.identityRepo && (report.clientId || context.activeClientId)) {
      try {
        const client = await this.identityRepo.getClient(report.clientId || context.activeClientId);
        if (client) {
          clientNameFromRepo = client.name;
        }
      } catch (e) { // Ignore error
      }
    }

    let refDocsList: any[] = [];
    if (typeof localStorage !== 'undefined' && report.inspectionId) {
      try {
        const storedDocs = localStorage.getItem(`ri_ref_docs_${report.inspectionId}`);
        if (storedDocs) {
          const parsedDocs = JSON.parse(storedDocs);
          if (Array.isArray(parsedDocs)) {
            refDocsList = parsedDocs.map((d: any) => ({
              name: d.name || 'Norma de Referência',
              description: d.description || '-',
              type: 'Documento Vinculado'
            }));
          }
        }
      } catch {
        // Safe fallback
      }
    }

    // Resolve real signatories from identity/professional repository without hardcoded names
    let inspectorProf: any = null;
    let plhProf: any = null;

    if (this.identityRepo) {
      if (report.inspectorId) {
        try {
          inspectorProf = await (this.identityRepo.getProfessional ? this.identityRepo.getProfessional(report.inspectorId) : this.identityRepo.findById?.(report.inspectorId));
        } catch {
          // Fall back to identifier
        }
      }
      if (report.engineerPlhId) {
        try {
          plhProf = await (this.identityRepo.getProfessional ? this.identityRepo.getProfessional(report.engineerPlhId) : this.identityRepo.findById?.(report.engineerPlhId));
        } catch {
          // Fall back to identifier
        }
      }
    }

    const inspectorDisplayName = inspectorProf?.name || (report as any).inspectorName || (inspection as any)?.inspectorName || report.inspectorId;
    const plhDisplayName = plhProf?.name || (report as any).engineerPlhName || (report as any).plhName || report.engineerPlhId;

    // Only include registered characters/numbers
    const inspectorCreds: string[] = [];
    if (inspectorProf?.cft) {
      inspectorCreds.push(inspectorProf.cft.toUpperCase().startsWith('CFT') ? inspectorProf.cft : `CFT: ${inspectorProf.cft}`);
    }
    const endIsoVal = inspectorProf?.endIso || inspectorProf?.endIso9712 || inspectorProf?.snqc;
    if (endIsoVal) {
      inspectorCreds.push(endIsoVal.toUpperCase().startsWith('END') || endIsoVal.toUpperCase().startsWith('ISO') ? endIsoVal : `END ISO 9712: ${endIsoVal}`);
    }
    if (inspectorProf?.crea) {
      inspectorCreds.push(inspectorProf.crea.toUpperCase().startsWith('CREA') ? inspectorProf.crea : `CREA: ${inspectorProf.crea}`);
    }
    const inspectorCredString = inspectorCreds.length > 0 ? inspectorCreds.join(' | ') : ((report as any).inspectorCrt || (report as any).inspectorCred || '');

    const plhCreds: string[] = [];
    if (plhProf?.crea) {
      plhCreds.push(plhProf.crea.toUpperCase().startsWith('CREA') ? plhProf.crea : `CREA: ${plhProf.crea}`);
    }
    if (plhProf?.cft) {
      plhCreds.push(plhProf.cft.toUpperCase().startsWith('CFT') ? plhProf.cft : `CFT: ${plhProf.cft}`);
    }
    const plhCredString = plhCreds.length > 0 ? plhCreds.join(' | ') : ((report as any).engineerPlhCrea || (report as any).plhCred || '');

    let inspectorSignatureBase64: string | null = null;
    let plhSignatureBase64: string | null = null;

    if (this.identityRepo && this.identityRepo.getProfessionalSignature) {
      if (report.inspectorId) {
        try { inspectorSignatureBase64 = await this.identityRepo.getProfessionalSignature(report.inspectorId); } catch {}
      }
      if (report.engineerPlhId) {
        try { plhSignatureBase64 = await this.identityRepo.getProfessionalSignature(report.engineerPlhId); } catch {}
      }
    }

    const signatories = {
      inspector: inspectorDisplayName ? {
        name: inspectorDisplayName,
        cred: inspectorCredString,
        signatureUrl: inspectorSignatureBase64 || inspectorProf?.signature || inspectorProf?.signatureImage || null
      } : null,
      plh: plhDisplayName ? {
        name: plhDisplayName,
        cred: plhCredString,
        signatureUrl: plhSignatureBase64 || plhProf?.signature || plhProf?.signatureImage || null
      } : null,
      fiscal: {
        name: 'FISCALIZAÇÃO',
        cred: 'FISCAL DO CONTRATO'
      }
    };

    const tenantLogo = this.visualHooks
      ? await this.visualHooks.logo(String(report.id), ReportService.VISUALLY_CLOSED.has(String(report.status)) || !!report.finalization).catch(() => null)
      : null;
    return UnifiedInspectionIntegrityReportMapper.mapToDocumentSnapshot({
      tenantLogo,
      report,
      inspection: inspection ? {
        ...inspection,
        projectName: projectData?.name || (inspection as any).projectName || null,
        projectCode: projectData?.code || (inspection as any).projectCode || null
      } : null,
      project: projectData,
      equipmentSnapshot,
      safetyDevices,
      instruments,
      checklist,
      checklistComponents,
      performedInspectionMethods,
      refDocs: refDocsList,
      recommendations,
      photos,
      signatories,
      client: clientNameFromRepo ? { name: clientNameFromRepo } : (report.clientId ? { name: '', id: report.clientId } : null),
      generatedLineList: await this.resolveGeneratedLineList(scopedContext, report, targetEquipmentId, isHistorical)
    });
  }

  private _lineLists: PipingSystemLineListService | null = null;
  private lineListServiceFor(): PipingSystemLineListService | null {
    if (!this.equipmentService) return null;
    if (!this._lineLists) this._lineLists = new PipingSystemLineListService(this.equipmentService);
    return this._lineLists;
  }

  /**
   * LINHAS QUE COMPOE O SISTEMA (comando complementar): só quando o
   * Equipamento do RI é um SISTEMA DE TUBULAÇÃO. Em rascunho reflete o
   * cadastro atual (fonte única: PipingSystemLineListService); a partir de
   * aprovado/finalizado/emitido (isHistorical) usa o que já foi congelado em
   * `report.lineListSnapshot` — cadastrar ou alterar Linha depois nunca muda
   * a revisão histórica.
   */
  private async resolveGeneratedLineList(context: RequestContext, report: IntegrityReport, equipmentId: string | null | undefined, isHistorical: boolean): Promise<GeneratedLineList | null> {
    if (report.lineListSnapshot) return report.lineListSnapshot;
    if (isHistorical || !equipmentId) return null;
    const service = this.lineListServiceFor();
    if (!service) return null;
    try {
      const equipment = await this.equipmentService.getEquipment(context, equipmentId);
      return await service.buildIfSystem(context, equipment);
    } catch {
      return null;
    }
  }

  async validateIntegrityReport(context: RequestContext, reportId: IntegrityReportId): Promise<{
    hasImpeditive: boolean;
    errors: { type: 'INFORMATIVE' | 'ATTENTION' | 'IMPEDITIVE'; message: string; componentId?: string }[]
  }> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Integrity Report not found');

    const errors: { type: 'INFORMATIVE' | 'ATTENTION' | 'IMPEDITIVE'; message: string; componentId?: string }[] = [];

    if (this.checklistRepo) {
      const checklist = await this.checklistRepo.findByInspection(context, report.inspectionId);
      if (!checklist) {
        errors.push({ type: 'IMPEDITIVE', message: 'O Checklist da inspeção não possui componentes persistidos.' });
      } else {
        const components = await this.checklistRepo.getComponents(context, checklist.id);
        if (components.length === 0) {
          errors.push({ type: 'IMPEDITIVE', message: 'O Checklist da inspeção não possui componentes persistidos.' });
        }
        for (const comp of components) {
          if (comp.result === 'PENDING' || !comp.result) {
            errors.push({ type: 'IMPEDITIVE', message: `O componente "${comp.definitionComponentId || comp.id}" está pendente de inspeção.`, componentId: comp.id });
          } else if (comp.result === 'REJECTED' && (!comp.description || comp.description.trim() === '')) {
            errors.push({ type: 'IMPEDITIVE', message: `O componente "${comp.definitionComponentId || comp.id}" está REPROVADO, mas não possui a Condição Encontrada descrita.`, componentId: comp.id });
          }
        }
      }
    }

    if (!report.inspectorId) {
      errors.push({ type: 'IMPEDITIVE', message: 'Falta assinalar o Inspetor responsável.' });
    }

    if (!report.equipmentSnapshotId) {
      errors.push({ type: 'IMPEDITIVE', message: 'Equipamento selecionado não possui dados ou o snapshot falhou.' });
    } else if (this.equipmentService) {
      try {
        const snapshot = await this.equipmentService.getSnapshot(context, report.equipmentSnapshotId);
        if (snapshot && snapshot.equipmentData) {
          const eq = snapshot.equipmentData;
          
          const dp = eq.designPressure?.value ?? eq.designPressure;
          if (dp === null || dp === undefined || dp === '') {
            errors.push({ type: 'IMPEDITIVE', message: 'Pressão de Projeto não cadastrada no equipamento.' });
          }

          const dt = eq.designTemperature?.value ?? eq.designTemperature;
          if (dt === null || dt === undefined || dt === '') {
            errors.push({ type: 'IMPEDITIVE', message: 'Temperatura de Projeto ausente no equipamento.' });
          }

          if (!eq.material || String(eq.material).trim() === '') {
            errors.push({ type: 'IMPEDITIVE', message: 'Material não informado no equipamento.' });
          }

          const op = eq.operatingPressure?.value ?? eq.operatingPressure;
          if (op === null || op === undefined || op === '') {
            errors.push({ type: 'ATTENTION', message: 'Equipamento selecionado não possui dados de operação (Pressão).' });
          }
        }
      } catch (err) {
        // Ignorar se falhar ao buscar snapshot
      }
    }

    if (this.projectRepo && this.inspectionRepo) {
      try {
        const insp = await this.inspectionRepo.findById(context, report.inspectionId);
        if (insp && (insp as any).projectId) {
          const proj = await this.projectRepo.getProject((insp as any).projectId);
          if (proj && !proj.engineerProfessionalId) {
            errors.push({ type: 'IMPEDITIVE', message: 'ENGENHEIRO/PLH RESPONSÁVEL PELO PROJETO NÃO DEFINIDO.' });
          }
        }
      } catch (err) {}
    }

    if (!report.engineeringAssessment?.equipmentCondition) {
      errors.push({ type: 'IMPEDITIVE', message: 'Informe a Condição Final da análise de Engenharia antes de concluir o relatório.' });
    }
    if (!report.conclusion || !report.conclusion.trim() || report.conclusion === 'NÃO INFORMADO') {
      errors.push({ type: 'IMPEDITIVE', message: 'Informe a Conclusão Técnica da análise de Engenharia antes de concluir o relatório.' });
    }

    return {
      hasImpeditive: errors.some(e => e.type === 'IMPEDITIVE'),
      errors
    };
  }

  async updateReport(context: RequestContext, report: IntegrityReport): Promise<void> {
    const existing = await this.reportRepo.findById(context, report.id);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, 'Integrity Report not found');
    }

    if (existing.finalization) {
      throw new AppError(ErrorCode.Conflict, 'REPORT_FINALIZED', { safeMessage: 'Relatório finalizado: a edição ordinária está bloqueada.' });
    }
    const lockedStatuses = ['FINALIZED_BY_INSPECTOR', 'FINALIZED', 'SUBMITTED_TO_ENGINEERING', 'UNDER_ENGINEERING_REVIEW'];
    if (lockedStatuses.includes(existing.status) && existing.status === report.status) {
      throw new AppError(ErrorCode.Authorization, 'REPORT_LOCKED: O relatório está finalizado e não pode ser alterado.', { safeMessage: 'Relatório bloqueado para edição.' });
    }

    // Inspetor só pode editar relatórios em que seja o inspetor responsável
    if (!context.permissions.has(PermissionCode.ReportReadAll) && context.professionalId) {
      if (existing.inspectorId && String(existing.inspectorId) !== String(context.professionalId)) {
        throw new AppError(ErrorCode.Authorization, "Você só possui permissão para editar relatórios sob sua responsabilidade técnica.");
      }
    }

    // Reatribuir o Inspetor Responsável exige permissão de Administração/Engenharia (Fase 7/9)
    if (report.inspectorId && existing.inspectorId && String(report.inspectorId) !== String(existing.inspectorId)) {
      if (!context.permissions.has(PermissionCode.ReportAssignInspector) && !context.permissions.has(PermissionCode.ReportReadAll)) {
        throw new AppError(ErrorCode.Authorization, "Você não possui permissão para reatribuir o Inspetor Responsável deste relatório.");
      }
    }

    // Autoria imutável: reatribuir o Inspetor NUNCA altera o criador original (Fase 8)
    report.createdBy = existing.createdBy;
    report.version++;
    report.updatedAt = new Date().toISOString();
    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: existing.status } as any,
      newState: { status: report.status } as any,
      metadata: { section: 'general' }
    });
  }

  async deleteReport(context: RequestContext, reportId: IntegrityReportId): Promise<{ reportId: IntegrityReportId; inspectionId: InspectionId; deleted: boolean }> {
    const hasAdminDelete = context.permissions.has(PermissionCode.ReportDelete);
    const hasUpdate = context.permissions.has(PermissionCode.ReportUpdate);

    if (!hasAdminDelete && !hasUpdate) {
      new RbacAuthorizationService().require(context, PermissionCode.ReportUpdate);
    }

    const existing = await this.reportRepo.findById(context, reportId);
    if (!existing) {
      throw new AppError(ErrorCode.NotFound, 'Report not found', { safeMessage: 'Relatório não encontrado para exclusão.' });
    }

    // Isolamento estrito de tenant: não permitir exclusão de relatório de outro cliente
    const activeClientId = context.activeClientId;
    if (existing.clientId && activeClientId && String(existing.clientId).toUpperCase() !== String(activeClientId).toUpperCase()) {
      throw new AppError(ErrorCode.NotFound, 'Report not found', { safeMessage: 'Relatório não encontrado para exclusão neste cliente.' });
    }

    // Validação canônica pela política de exclusão
    if (!canDeleteReport(existing, context)) {
      if (existing.status === 'SIGNED' || existing.status === 'COMPLETED') {
        throw new AppError(
          ErrorCode.Conflict,
          `REPORT_IMMUTABLE: O relatório no status ${existing.status} é um documento legal imutável e não pode ser excluído.`,
          { safeMessage: 'Relatórios assinados ou concluídos são documentos legais imutáveis e não podem ser excluídos fisicamente.' }
        );
      }
      if (!hasAdminDelete && existing.status !== 'DRAFT' && existing.status !== 'IN_ELABORATION') {
        throw new AppError(
          ErrorCode.Conflict,
          `REPORT_NOT_DELETABLE: O relatório no status ${existing.status} não pode ser excluído operacionalmente. Exclusão de relatórios em fluxo de engenharia requer perfil de Administrador (report.delete).`,
          { safeMessage: 'Somente relatórios em elaboração podem ser excluídos operacionalmente. Para relatórios em revisão, solicite a um Administrador.' }
        );
      }
      throw new AppError(
        ErrorCode.Authorization,
        'Você só possui permissão para excluir relatórios sob sua responsabilidade técnica.',
        { safeMessage: 'Você não possui permissão para excluir este relatório.' }
      );
    }

    const isAdministrative = hasAdminDelete && (existing.status !== 'DRAFT' && existing.status !== 'IN_ELABORATION');

    const scopedContext: RequestContext = {
      ...context,
      activeClientId: (existing.clientId || context.activeClientId) as any
    };

    console.log('[RI-DELETE]', {
      reportId,
      clientId: existing.clientId,
      inspectionId: existing.inspectionId,
      status: existing.status,
      isAdministrative,
      allowed: true
    });

    if (this.recommendationRepo && (this.recommendationRepo as any).deleteByIntegrityReport) {
      await (this.recommendationRepo as any).deleteByIntegrityReport(scopedContext, reportId);
    }

    // Exclusão atômica via repositório — propagar qualquer erro (NUNCA engolir)
    await this.reportRepo.delete(scopedContext, reportId);

    // Readback assert: confirmar que o relatório não existe mais
    const readBack = await this.reportRepo.findById(scopedContext, reportId);
    if (readBack) {
      throw new AppError(ErrorCode.Persistence, 'REPORT_DELETE_PERSISTENCE_MISMATCH: Falha ao persistir a exclusão do relatório no banco.', {
        safeMessage: 'Falha de persistência ao excluir o relatório.'
      });
    }

    // Limpeza de cache local somente após exclusão confirmada
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(`ri_checklist_${reportId}`);
        localStorage.removeItem(`ri_cache_${reportId}`);
        if (existing.inspectionId) {
          localStorage.removeItem(`ri_checklist_insp_${existing.inspectionId}`);
          localStorage.removeItem(`ri_performed_tests_${existing.inspectionId}`);
          localStorage.removeItem(`ri_ref_docs_${existing.inspectionId}`);
        }
      }
    } catch {
      // Ignore storage errors in non-browser environments
    }

    await this.audit?.record(context, {
      action: AuditAction.Delete,
      entityType: 'IntegrityReport',
      entityId: reportId,
      previousState: {
        status: existing.status,
        reportNumber: existing.reportNumber ?? null,
        inspectionId: existing.inspectionId
      } as any,
      newState: null,
      metadata: {
        action: isAdministrative ? 'REPORT_ADMINISTRATIVELY_DELETED' : 'REPORT_DELETED',
        reportNumber: existing.reportNumber ?? null,
        inspectionId: existing.inspectionId,
        previousStatus: existing.status,
        isAdministrative,
        deletedBy: context.userId,
        deletedAt: new Date().toISOString()
      }
    }).catch(() => {});

    return {
      reportId,
      inspectionId: existing.inspectionId,
      deleted: true
    };
  }

  async listRecommendationsByReport(context: RequestContext, reportId: IntegrityReportId): Promise<readonly Recommendation[]> {
    if (!this.recommendationRepo) return [];
    return this.recommendationRepo.findByIntegrityReport(context, reportId);
  }

  async createDraftReport(
    context: RequestContext,
    inspectionId: InspectionId
  ): Promise<IntegrityReport> {
    const clientId = context.activeClientId;
    if (!clientId) throw new AppError(ErrorCode.Authorization, 'No active client');

    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.ReportCreate);

    if (!this.inspectionRepo) {
      throw new AppError(ErrorCode.Internal, 'Inspection repository is required to create a report');
    }

    const inspection = await this.inspectionRepo.findById(context, inspectionId);
    if (!inspection) {
      throw new AppError(ErrorCode.NotFound, 'Inspection not found');
    }

    const existing = await this.reportRepo.findByInspection(context, inspectionId);
    
    const equipmentSnapshotId: string | null = inspection.equipmentSnapshotId;
    const checklistComponents: ChecklistComponent[] = [];
    const allPhotos: ChecklistPhoto[] = [];

    if (this.checklistRepo) {
      const checklist = await this.checklistRepo.findByInspection(context, inspectionId);
      if (checklist) {
        const components = await this.checklistRepo.getComponents(context, checklist.id);
        for (const comp of components) {
          checklistComponents.push(comp);
          const photos = await this.checklistRepo.getPhotos(context, comp.id);
          allPhotos.push(...photos);
        }
      }
    }

    const inspectionObservations = this.buildInspectionVisualFromChecklist(checklistComponents);

    if (existing) {
      if (existing.status !== 'DRAFT' && existing.status !== 'IN_ELABORATION') {
        throw new AppError(ErrorCode.Conflict, `Integrity Report already exists and is in status ${existing.status}`);
      }
      existing.inspectionObservations = inspectionObservations;
      existing.updatedAt = new Date().toISOString();
      await this.reportRepo.save(context, existing);
      return existing;
    }

    const year = new Date().getFullYear();
    const clientPrefix = clientId.substring(0, 3).toUpperCase();
    // P0-06: Generate real RI number at creation instead of placeholders
    let osNum = "001";
    let subOsNum = "01";
    try {
      if (this.inspectionRepo) {
        const insp = await this.inspectionRepo.findById(context, inspectionId);
        if (insp) {
          osNum = ((insp as any).osNumber || "001").replace(/\D/g, "") || "001";
          subOsNum = ((insp as any).subOsNumber || "01").replace(/\D/g, "") || "01";
        }
      }
    } catch (e) { console.warn("P0-06: Could not retrieve inspection for RI numbering:", e); }
    const allReports = await this.reportRepo.listAll(context);
    const clientReports = allReports.filter(r => r.clientId === clientId && r.reportNumber && !r.reportNumber.includes("RI-NOVO"));
    const seq = (clientReports.length + 1).toString().padStart(2, "0");
    const generatedReportNumber = `RI_${clientPrefix}_${osNum}_${subOsNum}_${year}_${seq}`;

    const report: IntegrityReport = {
      id: domainId<IntegrityReportId>(randomUUID()),
      reportNumber: generatedReportNumber,
      clientId,
      inspectionId,
      equipmentSnapshotId,
      status: 'DRAFT',
      objective: 'Avaliar as condições físicas e operacionais para garantir a conformidade com as normas vigentes e a segurança do processo.',
      referenceDocuments: null,
      inspectionObservations,
      maintenanceServices: null,
      nextInspectionDates: null,
      damageMechanisms: null,
      conclusion: null,
      inspectorId: inspection.inspectorId ?? null,
      engineerPlhId: null,
      sentToPlhAt: null,
      sentToPlhBy: null,
      returnedBy: null,
      returnedAt: null,
      returnComment: null,
      approvedByPlh: null,
      approvedAt: null,
      cancelledBy: null,
      cancelledAt: null,
      cancellationReason: null,
      version: 1,
      createdBy: context.userId ? String(context.userId) : "system",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Create,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: null,
      newState: { status: report.status, inspectionId } as any,
      metadata: { checklistComponents: checklistComponents.length, photos: allPhotos.length }
    });

    return report;
  }

  private buildInspectionVisualFromChecklist(components: readonly ChecklistComponent[]): string {
    const rejected = components.filter(c => c.result === 'REJECTED');
    if (rejected.length === 0) return '';

    return rejected.map(c => {
      let text = `Componente ${c.groupId} / ${c.definitionComponentId}: `;
      if (c.result === 'REJECTED') {
        text += (c.description || 'Sem descrição.');
        if (c.inspectionNotes) {
          text += ` Observação: ${c.inspectionNotes}.`;
        }
      }
      return text;
    }).join('\n\n');
  }

  async clearRecommendationsForReport(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<void> {
    if (this.recommendationRepo && (this.recommendationRepo as any).deleteByIntegrityReport) {
      await (this.recommendationRepo as any).deleteByIntegrityReport(context, reportId);
    }
  }

  async deleteRecommendation(
    context: RequestContext,
    recommendationId: RecommendationId
  ): Promise<void> {
    if (this.recommendationRepo && (this.recommendationRepo as any).delete) {
      await (this.recommendationRepo as any).delete(context, recommendationId);
    }
  }

  async addRecommendation(
    context: RequestContext,
    reportId: IntegrityReportId,
    req: {
      id?: string;
      discipline: string;
      criticality: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
      executeIn: 'ROUTINE' | 'SHUTDOWN';
      scope: string;
      zr?: string;
      componentId?: string;
      checklistItemId?: string;
      code?: string;
    }
  ): Promise<Recommendation> {
    const clientId = context.activeClientId;
    if (!clientId) throw new AppError(ErrorCode.Authorization, 'No active client');

    const report = await this.reportRepo.findById(context, reportId);
    if (!report) {
      throw new AppError(ErrorCode.NotFound, 'Integrity Report not found');
    }
    
    let equipmentId: EquipmentId | null = null;
    if (this.inspectionRepo) {
      const inspection = await this.inspectionRepo.findById(context, report.inspectionId);
      equipmentId = inspection?.equipmentId ?? null;
    }

    let existingRec: Recommendation | null = null;
    if (req.id && this.recommendationRepo) {
      existingRec = await this.recommendationRepo.findById(context, req.id as RecommendationId).catch(() => null);
    }

    let code = req.code || existingRec?.code;
    if (!code) {
      let existingRecs: readonly Recommendation[] = [];
      if (this.recommendationRepo) {
        existingRecs = await this.recommendationRepo.findByIntegrityReport(context, reportId);
      }
      const nextSeq = (existingRecs.length + 1).toString().padStart(3, '0');
      code = `REC-${nextSeq}`;
    }

    const recommendation: Recommendation = {
      id: existingRec ? existingRec.id : (req.id ? domainId<RecommendationId>(req.id) : domainId<RecommendationId>(randomUUID())),
      code,
      recommendationNumber: code,
      clientId,
      integrityReportId: report.id,
      inspectionId: report.inspectionId,
      equipmentId,
      componentId: req.componentId ?? null,
      componentName: (req as any).componentName ?? null,
      checklistItemId: req.checklistItemId ?? null,
      discipline: req.discipline,
      criticality: req.criticality,
      executeIn: req.executeIn,
      scope: req.scope,
      zr: req.zr ?? null,
      version: existingRec ? existingRec.version + 1 : 1,
      createdAt: existingRec ? existingRec.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    validateRecommendationCreation(recommendation);

    if (this.recommendationRepo) {
      await this.recommendationRepo.save(context, recommendation);
    }

    await this.audit?.record(context, {
      action: AuditAction.Create,
      entityType: 'Recommendation',
      entityId: recommendation.id,
      previousState: null,
      newState: { discipline: recommendation.discipline, criticality: recommendation.criticality, scope: recommendation.scope } as any,
      metadata: { integrityReportId: reportId, checklistItemId: recommendation.checklistItemId }
    });

    return recommendation;
  }

  /**
   * CONFIGURAÇÕES → NUMERAÇÃO. Com o alocador ligado, o número vem do modelo
   * configurado e de uma sequência ATÔMICA; sem ele (testes, composições que
   * não o fornecem), vale o gerador histórico.
   */
  private numbering: DocumentNumberAllocator | null = null;

  attachNumberAllocator(allocator: DocumentNumberAllocator): void {
    this.numbering = allocator;
  }

  /**
   * STUDIO INSPECT IA — o RI não guarda modelo congelado (é remontado a cada
   * impressão). Por isso a versão visual é CARIMBADA quando ele é aprovado ou
   * finalizado, e a impressão usa sempre a versão carimbada (versões
   * publicadas são imutáveis). RI fechado sem carimbo = anterior ao Studio =
   * texto INSPECT IA. Sem a ligação, nada muda.
   */
  private visualHooks: { stamp(reportId: string): Promise<unknown>; logo(reportId: string, closed: boolean): Promise<string | null> } | null = null;

  attachVisualHooks(hooks: { stamp(reportId: string): Promise<unknown>; logo(reportId: string, closed: boolean): Promise<string | null> }): void {
    this.visualHooks = hooks;
  }

  private static readonly VISUALLY_CLOSED = new Set(["APPROVED", "AWAITING_SIGNATURE", "SIGNED", "COMPLETED"]);

  private async stampVisual(reportId: string): Promise<void> {
    // O carimbo é aparência: uma falha aqui nunca impede a aprovação.
    await this.visualHooks?.stamp(reportId).catch(() => undefined);
  }

  async generateReportNumber(
    context: RequestContext,
    reportId: IntegrityReportId,
    options?: { projectCode?: string; formatMask?: string }
  ): Promise<string> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');

    if (report.reportNumber && !report.reportNumber.includes('RI-NOVO') && !report.reportNumber.includes('Elaboração')) {
      return report.reportNumber;
    }

    const year = new Date().getFullYear();
    const clientId = context.activeClientId || report.clientId;
    const clientPrefix = (clientId || 'IND').substring(0, 3).toUpperCase();

    const allReports = await this.reportRepo.listAll(context);
    const clientReports = allReports.filter(r => r.clientId === clientId && r.reportNumber && !r.reportNumber.includes('RI-NOVO'));
    const seq = (clientReports.length + 1).toString().padStart(2, '0');

    let os = '001';
    let subOs = '01';
    try {
      if (this.inspectionRepo) {
        const insp = await this.inspectionRepo.findById(context, report.inspectionId);
        if (insp) {
          os = ((insp as any).osNumber || '001').replace(/\D/g, '') || '001';
          subOs = ((insp as any).subOsNumber || '01').replace(/\D/g, '') || '01';
        }
      }
    } catch (e) {
      console.warn("Could not retrieve inspection for report number generation:", e);
    }

    // CONFIGURAÇÕES → NUMERAÇÃO: o modelo padrão do RI reproduz exatamente o formato histórico.
    const generated = this.numbering
      ? await this.numbering.allocate({
          documentType: "RI", clientId: clientId || null,
          projectId: (report as { projectId?: string | null }).projectId ?? null,
          unitAreaId: (report as { unitAreaId?: string | null }).unitAreaId ?? null,
          revision: (report as { revision?: string | null }).revision ?? "00", osNumber: os, subOsNumber: subOs,
          legacyCount: async () => clientReports.length,
          isTaken: async (candidate, scope) => (scope.perClient ? clientReports : allReports).some(r => r.reportNumber === candidate)
        })
      : `RI_${clientPrefix}_${os}_${subOs}_${year}_${seq}`;

    report.reportNumber = generated;
    report.updatedAt = new Date().toISOString();
    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: null,
      newState: { reportNumber: generated } as any,
      metadata: { generatedReportNumber: generated }
    });

    return generated;
  }

  async createRecommendationForInspection(
    context: RequestContext,
    payload: {
      reportId: IntegrityReportId;
      inspectionId: InspectionId;
      equipmentId?: EquipmentId | null;
      projectId?: string | null;
      unitAreaId?: string | null;
      componentId?: string | null;
      componentName?: string | null;
      checklistItemId?: string | null;
      discipline: string;
      criticality: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';
      executeIn: 'ROUTINE' | 'SHUTDOWN';
      scope: string;
      zr?: string | null;
    }
  ): Promise<Recommendation> {
    const clientId = context.activeClientId;
    if (!clientId) throw new AppError(ErrorCode.Authorization, 'No active client');

    const report = await this.reportRepo.findById(context, payload.reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Integrity Report not found');

    let existingRecs: readonly Recommendation[] = [];
    if (this.recommendationRepo) {
      existingRecs = await this.recommendationRepo.findByIntegrityReport(context, payload.reportId);
    }
    const nextSeq = (existingRecs.length + 1).toString().padStart(3, '0');
    const recCode = `REC-${nextSeq}`;

    const recId = domainId<RecommendationId>(`rec-${payload.reportId}-${randomUUID().slice(0, 8)}`);

    const recommendation: Recommendation = {
      id: recId,
      code: recCode,
      clientId,
      integrityReportId: report.id,
      inspectionId: payload.inspectionId,
      projectId: payload.projectId ?? null,
      unitAreaId: payload.unitAreaId ?? null,
      equipmentId: payload.equipmentId ?? null,
      componentId: payload.componentId ?? null,
      componentName: payload.componentName ?? null,
      checklistItemId: payload.checklistItemId ?? null,
      discipline: payload.discipline,
      criticality: payload.criticality,
      executeIn: payload.executeIn,
      scope: payload.scope,
      zr: payload.zr ?? null,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    validateRecommendationCreation(recommendation);

    if (this.recommendationRepo) {
      await this.recommendationRepo.save(context, recommendation);
    }

    await this.audit?.record(context, {
      action: AuditAction.Create,
      entityType: 'Recommendation',
      entityId: recommendation.id,
      previousState: null,
      newState: { code: recCode, discipline: recommendation.discipline, criticality: recommendation.criticality, scope: recommendation.scope } as any,
      metadata: { integrityReportId: payload.reportId, code: recCode, inspectionId: payload.inspectionId }
    });

    return recommendation;
  }

  /**
   * Atribuição/alteração direta do ZR pelo fluxo de cadastro da Recommendation.
   * A edição controlada da Engenharia (com motivo obrigatório e histórico) é
   * updateRecommendationZrNumber.
   */
  async updateRecommendationZR(
    context: RequestContext,
    recommendationId: RecommendationId,
    zr: string
  ): Promise<Recommendation> {
    if (!this.recommendationRepo) throw new AppError(ErrorCode.NotFound, 'Recommendation repository not available');
    const rec = await this.recommendationRepo.findById(context, recommendationId);
    if (!rec) throw new AppError(ErrorCode.NotFound, 'Recommendation not found');

    const previousZR = rec.zr;
    const normalized = normalizeZrNumber(zr);

    if (normalized) {
      const siblings = await this.recommendationRepo.findByIntegrityReport(context, rec.integrityReportId);
      assertZrNotDuplicated(normalized, siblings as any, rec.id);
    }

    rec.zr = normalized || null;
    rec.zrHistory = appendZrHistory(rec.zrHistory, {
      action: previousZR ? 'UPDATED' : 'ASSIGNED',
      previousZr: previousZR ?? null,
      newZr: rec.zr,
      reason: '',
      performedAt: new Date().toISOString(),
      performedByUserId: context.userId || null,
      performedByProfessionalId: context.professionalId ?? null
    });
    rec.updatedAt = new Date().toISOString();
    rec.version++;

    await this.recommendationRepo.save(context, rec);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'Recommendation',
      entityId: rec.id,
      previousState: { zr: previousZR } as any,
      newState: { zr: rec.zr } as any,
      metadata: { action: 'RECOMMENDATION_ZR_NUMBER_ASSIGNED', code: rec.code ?? null }
    });

    return rec;
  }

  /**
   * EDITAR Nº ZR — ação de Engenharia.
   *
   * UPDATE no registro existente: id, inspeção, componente, relatório e
   * vínculos permanecem; só o número muda. O motivo é obrigatório e o número
   * anterior fica no histórico.
   */
  async updateRecommendationZrNumber(
    context: RequestContext,
    recommendationId: RecommendationId,
    input: { newZr: string; reason: string }
  ): Promise<Recommendation> {
    new RbacAuthorizationService().require(context, PermissionCode.EngineeringEditZrNumber);
    if (!this.recommendationRepo) throw new AppError(ErrorCode.NotFound, 'Recommendation repository not available');

    const rec = await this.recommendationRepo.findById(context, recommendationId);
    if (!rec) throw new AppError(ErrorCode.NotFound, 'Recommendation not found', {
      safeMessage: 'Recomendação não encontrada.'
    });

    const newZr = requireZrNumber(input.newZr);
    const reason = requireZrReason(input.reason);

    const siblings = await this.recommendationRepo.findByIntegrityReport(context, rec.integrityReportId);
    assertZrNotDuplicated(newZr, siblings as any, rec.id);

    const previousZr = rec.zr ?? null;
    rec.zr = newZr;
    rec.zrHistory = appendZrHistory(rec.zrHistory, {
      action: previousZr ? 'UPDATED' : 'ASSIGNED',
      previousZr,
      newZr,
      reason,
      performedAt: new Date().toISOString(),
      performedByUserId: context.userId || null,
      performedByProfessionalId: context.professionalId ?? null
    });
    rec.updatedAt = new Date().toISOString();
    rec.version++;

    await this.recommendationRepo.save(context, rec);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'Recommendation',
      entityId: rec.id,
      previousState: { zr: previousZr } as any,
      newState: { zr: newZr } as any,
      metadata: {
        action: 'RECOMMENDATION_ZR_NUMBER_UPDATED',
        recommendationId: rec.id,
        oldZrNumber: previousZr,
        newZrNumber: newZr,
        reason,
        performedByUserId: context.userId || null,
        performedByProfessionalId: context.professionalId ?? null,
        performedAt: rec.updatedAt
      }
    });

    return rec;
  }

  /**
   * REMOVER Nº ZR — ação de Engenharia.
   *
   * A Recommendation NÃO é excluída: ela perde o número e volta para a fila de
   * pendência de numeração, com o número anterior preservado no histórico.
   */
  async removeRecommendationZrNumber(
    context: RequestContext,
    recommendationId: RecommendationId,
    input: { reason: string }
  ): Promise<Recommendation> {
    new RbacAuthorizationService().require(context, PermissionCode.EngineeringRemoveZrNumber);
    if (!this.recommendationRepo) throw new AppError(ErrorCode.NotFound, 'Recommendation repository not available');

    const rec = await this.recommendationRepo.findById(context, recommendationId);
    if (!rec) throw new AppError(ErrorCode.NotFound, 'Recommendation not found', {
      safeMessage: 'Recomendação não encontrada.'
    });

    if (isPendingZr(rec)) {
      throw new AppError(ErrorCode.Validation, 'Recommendation already pending ZR', {
        safeMessage: ZR_ALREADY_PENDING_MESSAGE
      });
    }

    const reason = requireZrReason(input.reason, ZR_REMOVAL_REASON_REQUIRED_MESSAGE);
    const removedZr = rec.zr as string;

    // Só o número sai. Nenhum outro campo é tocado e o id continua o mesmo.
    rec.zr = null;
    rec.zrHistory = appendZrHistory(rec.zrHistory, {
      action: 'REMOVED',
      previousZr: removedZr,
      newZr: null,
      reason,
      performedAt: new Date().toISOString(),
      performedByUserId: context.userId || null,
      performedByProfessionalId: context.professionalId ?? null
    });
    rec.updatedAt = new Date().toISOString();
    rec.version++;

    await this.recommendationRepo.save(context, rec);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'Recommendation',
      entityId: rec.id,
      previousState: { zr: removedZr } as any,
      newState: { zr: null } as any,
      metadata: {
        action: 'RECOMMENDATION_ZR_NUMBER_REMOVED',
        recommendationId: rec.id,
        removedZrNumber: removedZr,
        reason,
        performedByUserId: context.userId || null,
        performedByProfessionalId: context.professionalId ?? null,
        performedAt: rec.updatedAt
      }
    });

    return rec;
  }

  /**
   * Fila de pendência: recomendações do relatório que estão sem numeração —
   * inclusive as que acabaram de ter o ZR removido.
   */
  async listRecommendationsPendingZr(
    context: RequestContext,
    integrityReportId: IntegrityReportId
  ): Promise<Recommendation[]> {
    if (!this.recommendationRepo) throw new AppError(ErrorCode.NotFound, 'Recommendation repository not available');
    const all = await this.recommendationRepo.findByIntegrityReport(context, integrityReportId);
    return (all as Recommendation[]).filter(rec => isPendingZr(rec));
  }

  async updateRecommendationDescription(
    context: RequestContext,
    recommendationId: RecommendationId,
    description: string
  ): Promise<Recommendation> {
    if (!this.recommendationRepo) throw new AppError(ErrorCode.NotFound, 'Recommendation repository not available');
    const rec = await this.recommendationRepo.findById(context, recommendationId);
    if (!rec) throw new AppError(ErrorCode.NotFound, 'Recommendation not found');

    const trimmed = (description || '').trim();
    if (!trimmed) {
      throw new AppError(ErrorCode.Validation, 'Informe o descritivo da recomendação.');
    }

    const previousScope = rec.scope;
    rec.scope = trimmed;
    rec.updatedAt = new Date().toISOString();
    rec.version++;

    await this.recommendationRepo.save(context, rec);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'Recommendation',
      entityId: rec.id,
      previousState: { scope: previousScope } as any,
      newState: { scope: rec.scope } as any,
      metadata: { code: rec.code ?? null, action: 'RECOMMENDATION_DESCRIPTION_UPDATED' }
    });

    return rec;
  }

  async updateRecommendationFull(
    context: RequestContext,
    recommendationId: RecommendationId,
    payload: UpdateRecommendationFullPayload
  ): Promise<Recommendation> {
    if (!this.recommendationRepo) throw new AppError(ErrorCode.NotFound, 'Recommendation repository not available');
    
    const rec = await this.recommendationRepo.findById(context, recommendationId);
    if (!rec) throw new AppError(ErrorCode.NotFound, 'Recommendation not found');

    if (payload.version !== undefined && rec.version !== payload.version) {
      throw new AppError(ErrorCode.Validation, 'Esta recomendação foi alterada por outro usuário. Atualize a página antes de salvar.');
    }

    const previousState = { ...rec };

    if (payload.scope !== undefined) rec.scope = payload.scope.trim();
    if (payload.location !== undefined) rec.location = payload.location?.trim() || null;
    if (payload.dimension !== undefined) rec.dimension = payload.dimension?.trim() || null;
    if (payload.discipline !== undefined) rec.discipline = payload.discipline;
    if (payload.criticality !== undefined) rec.criticality = payload.criticality as any;
    if (payload.executeIn !== undefined) rec.executeIn = payload.executeIn as any;
    if (payload.technicalData !== undefined) rec.technicalData = payload.technicalData?.trim() || null;
    if (payload.observations !== undefined) rec.observations = payload.observations?.trim() || null;

    rec.updatedAt = new Date().toISOString();
    rec.version++;

    await this.recommendationRepo.save(context, rec);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'Recommendation',
      entityId: rec.id,
      previousState: previousState as any,
      newState: rec as any,
      metadata: { 
        code: rec.code ?? null, 
        action: 'RECOMMENDATION_FULL_UPDATED',
        correctionSource: payload.correctionSource || 'UNKNOWN'
      }
    });

    return rec;
  }

  async finalizeReport(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<{ success: boolean; blockingIssues: string[]; report: IntegrityReport }> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');

    const blockingIssues: string[] = [];

    if (this.checklistRepo) {
      const scopedContext: RequestContext = {
        ...context,
        activeClientId: (report.clientId || context.activeClientId) as any
      };
      let checklist = await this.checklistRepo.findByInspection(scopedContext, report.inspectionId);
      if (!checklist && report.id && String(report.id) !== String(report.inspectionId)) {
        const legacyChk = await this.checklistRepo.findByInspection(scopedContext, report.id as any);
        if (legacyChk) {
          legacyChk.inspectionId = report.inspectionId;
          legacyChk.updatedAt = new Date().toISOString();
          await this.checklistRepo.save(scopedContext, legacyChk);
          checklist = legacyChk;
        }
      }
      if (!checklist) {
        blockingIssues.push('O Checklist da inspeção não possui componentes persistidos.');
      } else {
        const components = await this.checklistRepo.getComponents(scopedContext, checklist.id);
        if (components.length === 0) {
          blockingIssues.push('O Checklist da inspeção não possui componentes persistidos.');
        }
        const pendingComps = components.filter(c => !c.result || c.result === 'PENDING');
        if (pendingComps.length > 0) {
          blockingIssues.push(`Existem ${pendingComps.length} componente(s) pendente(s) de avaliação técnica.`);
        }
        const rejectedWithoutComment = components.filter(c => c.result === 'REJECTED' && (!c.description || c.description.trim() === ''));
        if (rejectedWithoutComment.length > 0) {
          blockingIssues.push(`Existem ${rejectedWithoutComment.length} componente(s) REPROVADO(S) sem detalhamento técnico da não conformidade.`);
        }
      }
    }

    if (blockingIssues.length > 0) {
      return { success: false, blockingIssues, report };
    }

    report.status = 'FINALIZED_BY_INSPECTOR';
    report.updatedAt = new Date().toISOString();
    report.version++;
    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.StatusChange,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'IN_ELABORATION' },
      newState: { status: 'FINALIZED_BY_INSPECTOR' },
      metadata: { finalizedAt: new Date().toISOString() }
    });

    if (report.engineerPlhId && this.identityRepo) {
      try {
        const plh = await this.identityRepo.getProfessional(report.engineerPlhId);
        if (plh && plh.userId) {
          await notificationService.createNotification({
            userId: plh.userId,
            type: 'INFO',
            event: 'REPORT_FINALIZED',
            title: 'Relatório disponível para análise',
            message: `O ${report.reportNumber || 'Relatório'} foi finalizado pelo Inspetor.`,
            entityType: 'Report',
            entityId: report.id,
            clientId: report.clientId,
            idempotencyKey: `REPORT_FINALIZED_${report.id}_${report.version}`
          });
        }
      } catch (err) {
        console.warn('Falha ao gerar notificação para PLH', err);
      }
    }

    return { success: true, blockingIssues: [], report };
  }

  /** INSPECT IA REVIEW — revisão IA obrigatória antes de enviar o RI ao PLH. */
  private aiReviewGate: ((context: RequestContext, documentId: string) => Promise<void>) | null = null;

  attachAiReviewGate(gate: (context: RequestContext, documentId: string) => Promise<void>): void {
    this.aiReviewGate = gate;
  }

  async sendToPlh(
    context: RequestContext,
    reportId: IntegrityReportId,
    plhProfessionalId?: string
  ): Promise<IntegrityReport> {
    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'DRAFT' && report.status !== 'IN_ELABORATION' && report.status !== 'FINALIZED_BY_INSPECTOR' && report.status !== 'RETURNED' && report.status !== 'IN_CORRECTION') {
      throw new AppError(ErrorCode.Conflict, 'Report is not in a valid state to be sent to PLH');
    }

    if (!report.inspectorId) throw new AppError(ErrorCode.Validation, 'Inspector is required');
    if (this.aiReviewGate) await this.aiReviewGate(context, String(report.id));

    report = await this.syncEngineerFromProject(report, context);
    if (plhProfessionalId) {
      report.engineerPlhId = plhProfessionalId as ProfessionalId;
    }
    if (!report.engineerPlhId) {
      throw new AppError(ErrorCode.Validation, 'CLIENT_WITHOUT_PLH', { safeMessage: CLIENT_WITHOUT_PLH });
    }
    await this.assertPlhQualified(report);
    await this.freezeResponsibility(report, context);

    report.status = 'SUBMITTED_TO_ENGINEERING';
    report.sentToPlhAt = new Date().toISOString();
    report.sentToPlhBy = context.userId;
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.StatusChange,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'DRAFT' },
      newState: { status: 'SUBMITTED_TO_ENGINEERING', engineerPlhId: report.engineerPlhId },
      metadata: {}
    });

    return report;
  }

  async releaseReportToPLH(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<{ success: boolean; blockingIssues: string[]; report: IntegrityReport }> {
    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');

    const blockingIssues: string[] = [];

    if (this.checklistRepo) {
      const scopedContext: RequestContext = {
        ...context,
        activeClientId: (report.clientId || context.activeClientId) as any
      };
      let checklist = await this.checklistRepo.findByInspection(scopedContext, report.inspectionId);
      if (!checklist && report.id && String(report.id) !== String(report.inspectionId)) {
        const legacyChk = await this.checklistRepo.findByInspection(scopedContext, report.id as any);
        if (legacyChk) {
          legacyChk.inspectionId = report.inspectionId;
          legacyChk.updatedAt = new Date().toISOString();
          await this.checklistRepo.save(scopedContext, legacyChk);
          checklist = legacyChk;
        }
      }
      if (!checklist) {
        blockingIssues.push('O Checklist da inspeção não possui componentes persistidos.');
      } else {
        const components = await this.checklistRepo.getComponents(scopedContext, checklist.id);
        console.log('[CHECKLIST-RELEASE]', {
          reportInspectionId: report.inspectionId,
          persistedComponentCount: components.length
        });
        if (components.length === 0) {
          blockingIssues.push('O Checklist da inspeção não possui componentes persistidos.');
        }
        const pendingComps = components.filter(c => !c.result || c.result === 'PENDING');
        if (pendingComps.length > 0) {
          blockingIssues.push(`Existem ${pendingComps.length} componente(s) pendente(s) de avaliação técnica.`);
        }
        const rejectedWithoutComment = components.filter(c => c.result === 'REJECTED' && (!c.description || c.description.trim() === ''));
        if (rejectedWithoutComment.length > 0) {
          blockingIssues.push(`Existem ${rejectedWithoutComment.length} componente(s) REPROVADO(S) sem detalhamento técnico da não conformidade.`);
        }
      }
    }

    if (blockingIssues.length > 0) {
      return { success: false, blockingIssues, report };
    }

    report = await this.syncEngineerFromProject(report, context);
    if (!report.engineerPlhId) {
      blockingIssues.push(CLIENT_WITHOUT_PLH);
      return { success: false, blockingIssues, report };
    }
    try {
      await this.assertPlhQualified(report);
    } catch (e: any) {
      blockingIssues.push(e?.safeMessage || e?.message || 'PLH do Cliente inválido.');
      return { success: false, blockingIssues, report };
    }
    await this.freezeResponsibility(report, context);

    report.status = 'SUBMITTED_TO_ENGINEERING';
    report.sentToPlhAt = new Date().toISOString();
    report.sentToPlhBy = context.userId;
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.StatusChange,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'IN_ELABORATION' },
      newState: { status: 'SUBMITTED_TO_ENGINEERING', engineerPlhId: report.engineerPlhId },
      metadata: { action: 'REPORT_RELEASED_TO_PLH', releasedAt: new Date().toISOString() }
    });

    if (report.engineerPlhId && this.identityRepo) {
      try {
        const plh = await this.identityRepo.getProfessional(report.engineerPlhId);
        if (plh && plh.userId) {
          await notificationService.createNotification({
            userId: plh.userId,
            type: 'INFO',
            event: 'REPORT_FINALIZED',
            title: 'Relatório disponível para análise',
            message: `O relatório ${report.reportNumber || 'RI'} foi liberado pelo Inspetor para sua análise.`,
            entityType: 'Report',
            entityId: report.id,
            clientId: report.clientId,
            idempotencyKey: `REPORT_RELEASED_${report.id}_${report.version}`
          });
        }
      } catch (err) {
        console.warn('Falha ao gerar notificação para PLH na liberação', err);
      }
    }

    return { success: true, blockingIssues: [], report };
  }

  async returnByPlh(
    context: RequestContext,
    reportId: IntegrityReportId,
    returnComment: string
  ): Promise<IntegrityReport> {
    if (!returnComment || returnComment.trim() === '') {
      throw new AppError(ErrorCode.Validation, 'Return comment is mandatory');
    }
    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');

    if (report.status === 'SIGNED' || report.status === 'COMPLETED') {
      throw new AppError(ErrorCode.Conflict, 'Documentos assinados ou concluídos são imutáveis. Crie uma nova revisão documental para realizar alterações.', {
        safeMessage: 'Documentos assinados ou concluídos são imutáveis. Crie uma nova revisão documental para realizar alterações.'
      });
    }

    const allowedReturnStatuses = ['SUBMITTED_TO_ENGINEERING', 'UNDER_ENGINEERING_REVIEW', 'APPROVED', 'AWAITING_SIGNATURE'];
    if (!allowedReturnStatuses.includes(report.status)) {
      throw new AppError(ErrorCode.Conflict, 'Report must be under engineering or awaiting signature to be returned');
    }

    report = await this.syncEngineerFromProject(report, context);

    let userProfessionalId: string | null = null;
    if (this.identityRepo && context.userId) {
      try {
        const user = await this.identityRepo.getUser(context.userId);
        if (user && user.professionalId) {
          userProfessionalId = user.professionalId;
        }
      } catch {}
    }

    if (report.engineerPlhId && userProfessionalId && report.engineerPlhId !== userProfessionalId) {
      throw new AppError(ErrorCode.Authorization, 'Apenas o Engenheiro/PLH responsável pelo Projeto pode atuar neste relatório.');
    }

    const previousStatus = report.status;
    report.status = 'RETURNED';
    report.returnedAt = new Date().toISOString();
    report.returnedBy = context.userId;
    report.returnComment = returnComment;
    report.approvedByPlh = null;
    report.approvedAt = null;
    report.documentHash = null;
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.ReturnForCorrection,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: previousStatus },
      newState: { status: 'RETURNED', returnComment, previousApprovalRevoked: previousStatus === 'AWAITING_SIGNATURE' || previousStatus === 'APPROVED' },
      metadata: { previousStatus, returnComment }
    });

    if (report.inspectorId && this.identityRepo) {
      try {
        const inspector = await this.identityRepo.getProfessional(report.inspectorId);
        if (inspector && inspector.userId) {
          await notificationService.createNotification({
            userId: inspector.userId,
            type: 'WARNING',
            event: 'REPORT_RETURNED',
            title: 'Relatório devolvido para correção',
            message: `O ${report.reportNumber || 'Relatório'} foi devolvido pelo PLH. Motivo: ${returnComment}`,
            entityType: 'Report',
            entityId: report.id,
            clientId: report.clientId,
            idempotencyKey: `REPORT_RETURNED_${report.id}_${report.version}`
          });
        }
      } catch (err) {
        console.warn('Falha ao gerar notificação para Inspetor', err);
      }
    }

    return report;
  }

  async reopenForReview(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');

    if (report.status === 'SIGNED' || report.status === 'COMPLETED') {
      throw new AppError(ErrorCode.Conflict, 'Documentos assinados ou concluídos são imutáveis. Crie uma nova revisão documental para realizar alterações.', {
        safeMessage: 'Documentos assinados ou concluídos são imutáveis. Crie uma nova revisão documental para realizar alterações.'
      });
    }

    if (report.status !== 'AWAITING_SIGNATURE' && report.status !== 'APPROVED') {
      throw new AppError(ErrorCode.Conflict, 'Apenas relatórios aprovados ou aguardando assinatura podem ser reabertos para análise.');
    }

    const previousStatus = report.status;
    report.status = 'UNDER_ENGINEERING_REVIEW';
    report.approvedByPlh = null;
    report.approvedAt = null;
    report.documentHash = null;
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.StatusChange,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: previousStatus },
      newState: { status: 'UNDER_ENGINEERING_REVIEW' },
      metadata: { reason: 'Reopened for review after approval/signature' }
    });

    return report;
  }

  /**
   * "Editar Relatório" (Bloco 9): Engineering editing RI-exclusive fields
   * during their own review. Deliberately separate from updateReport()
   * above, which locks editing while status is UNDER_ENGINEERING_REVIEW —
   * that lock exists to stop the original elaborator from changing the
   * report out from under Engineering, not to stop Engineering itself from
   * editing during its own analysis. Only RI-exclusive fields are accepted
   * here (never TAG/Project/Unit-Area/Recommendation — those belong to
   * Equipment/Project/Checklist and must be edited at their real source,
   * per Fase 12).
   */
  async editReportFields(
    context: RequestContext,
    reportId: IntegrityReportId,
    payload: {
      objective?: string | null;
      referenceDocuments?: string | null;
      inspectionObservations?: string | null;
      maintenanceServices?: string | null;
      nextInspectionDates?: string | null;
      conclusion?: string | null;
    }
  ): Promise<IntegrityReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.IntegrityReportEdit);

    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'SUBMITTED_TO_ENGINEERING' && report.status !== 'UNDER_ENGINEERING_REVIEW') {
      throw new AppError(ErrorCode.Conflict, 'O relatório deve estar em análise pela Engenharia para ser editado.', {
        safeMessage: 'O relatório deve estar em análise pela Engenharia para ser editado.'
      });
    }

    if (payload.objective !== undefined) report.objective = payload.objective;
    if (payload.referenceDocuments !== undefined) report.referenceDocuments = payload.referenceDocuments;
    if (payload.inspectionObservations !== undefined) report.inspectionObservations = payload.inspectionObservations;
    if (payload.maintenanceServices !== undefined) report.maintenanceServices = payload.maintenanceServices;
    if (payload.nextInspectionDates !== undefined) report.nextInspectionDates = payload.nextInspectionDates;
    if (payload.conclusion !== undefined) report.conclusion = payload.conclusion;

    report.version++;
    report.updatedAt = new Date().toISOString();
    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Update,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: null,
      newState: null,
      metadata: { action: 'ENGINEERING_EDITED_REPORT_FIELDS' }
    });

    return report;
  }

  /** PLH do Projeto inativo ou fora do gate: bloqueia o envio com o motivo. */
  private async assertPlhQualified(report: IntegrityReport): Promise<void> {
    if (!report.engineerPlhId || !this.identityRepo) return;
    const plh = await this.identityRepo.getProfessional(report.engineerPlhId).catch(() => null);
    if (!plh) throw new AppError(ErrorCode.Validation, 'PLH not found', { safeMessage: 'O PLH responsável do Projeto não foi encontrado no cadastro de Profissionais.' });
    const gate = isPlhQualifiedProfessional(plh, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!gate.allowed) throw new AppError(ErrorCode.Validation, 'PLH not qualified', { safeMessage: `O PLH responsável do Projeto não está habilitado: ${gate.reason || ''}` });
  }

  /**
   * FINALIZAR RELATÓRIO (RI): só com análise concluída (validado/aprovado),
   * condição e Vida Residual definidas, Minuta validada, sem correção
   * pendente e fora de HOLD — o gate é decidido pelo EngineeringService; aqui
   * só se grava o snapshot final e o bloqueio. FINALIZAR ≠ ASSINAR.
   */
  async finalizeIntegrityReport(context: RequestContext, reportId: IntegrityReportId, input: { snapshotId: string | null }): Promise<IntegrityReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.IntegrityReportValidate);
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.finalization) throw new AppError(ErrorCode.Conflict, 'Already finalized', { safeMessage: 'O relatório já está finalizado.' });
    const now = new Date().toISOString();
    let name = context.userId || 'system';
    if (this.identityRepo && context.userId) {
      try { const user = await this.identityRepo.getUser(context.userId); const prof = user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null; name = prof?.name || user?.name || name; } catch { /* mantém id */ }
    }
    const signed = report.status === 'SIGNED' || report.status === 'COMPLETED';
    report.finalization = { finalizedAt: now, finalizedByUserId: context.userId || 'system', finalizedByName: name, revision: report.revision || '00', snapshotId: input.snapshotId, state: signed ? 'FINALIZADO' : 'FINALIZADO_AGUARDANDO_ASSINATURA' };
    await this.stampVisual(String(report.id));
    if (signed) report.status = 'COMPLETED';
    // LINHAS QUE COMPOE O SISTEMA: congela a composição desta revisão na
    // FINALIZAÇÃO (comando complementar, item 12) — cadastrar ou alterar
    // Linha depois nunca muda o RI já finalizado.
    if (!report.lineListSnapshot) {
      report.lineListSnapshot = await this.freezeLineListSnapshot(context, report);
    }
    report.version++;
    report.updatedAt = now;
    await this.reportRepo.save(context, report);
    await this.audit?.record(context, {
      action: AuditAction.DocumentFinalized,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: report.status } as any,
      newState: { finalization: report.finalization } as any,
      metadata: { reportNumber: report.reportNumber ?? null, revision: report.revision ?? '00', state: report.finalization.state, snapshotId: input.snapshotId, performedBy: context.userId ?? null }
    });
    return report;
  }

  /** Resolve o Equipamento do RI pela Inspeção e congela a Lista de Linhas, quando aplicável (Sistema de Tubulação). `null` para qualquer outro tipo — nada a congelar. */
  private async freezeLineListSnapshot(context: RequestContext, report: IntegrityReport): Promise<GeneratedLineList | null> {
    const service = this.lineListServiceFor();
    if (!service || !this.inspectionRepo) return null;
    try {
      const inspection = await this.inspectionRepo.findById(context, report.inspectionId);
      const equipmentId = (inspection as any)?.equipmentId;
      if (!equipmentId) return null;
      const equipment = await this.equipmentService.getEquipment(context, equipmentId);
      return await service.buildIfSystem(context, equipment);
    } catch {
      return null;
    }
  }

  /**
   * AVALIAÇÃO DE ENGENHARIA (Painel de Engenharia): condição do equipamento
   * com resultado derivado (nunca gravado por fora) e Vida Residual (AVR ou
   * Engenheiro). UPDATE no próprio RI durante a análise; auditado.
   */
  async setEngineeringAssessment(
    context: RequestContext,
    reportId: IntegrityReportId,
    assessment: EngineeringAssessment
  ): Promise<IntegrityReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.IntegrityReportEdit);
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'SUBMITTED_TO_ENGINEERING' && report.status !== 'UNDER_ENGINEERING_REVIEW' && report.status !== 'APPROVED') {
      throw new AppError(ErrorCode.Conflict, 'O relatório deve estar em análise pela Engenharia para receber a avaliação.', {
        safeMessage: 'O relatório deve estar em análise pela Engenharia para receber a avaliação.'
      });
    }
    const previous = report.engineeringAssessment ?? null;
    report.engineeringAssessment = normalizeEngineeringAssessment({ ...assessment, updatedAt: new Date().toISOString(), updatedBy: context.userId || null });
    report.version++;
    report.updatedAt = new Date().toISOString();
    await this.reportRepo.save(context, report);
    await this.audit?.record(context, {
      action: AuditAction.IntegrityAssessmentUpdated,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: previous as any,
      newState: report.engineeringAssessment as any,
      metadata: { reportNumber: report.reportNumber ?? null, condition: report.engineeringAssessment.equipmentCondition, result: report.engineeringAssessment.equipmentResult, residualLifeOrigin: report.engineeringAssessment.residualLife?.origin ?? null, adoptedMonths: report.engineeringAssessment.residualLife?.adoptedMonths ?? null, calculatedMonths: report.engineeringAssessment.residualLife?.calculatedMonths ?? null, performedBy: context.userId ?? null }
    });
    return report;
  }

  /**
   * "Validar Relatório" (Bloco 9): Engineering declares the RI technically
   * conforme, mirroring "Validar Minuta" (Bloco 6). Distinct from
   * approveByPlh() below — this is not the final signature.
   */
  async validateReport(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    const auth = new RbacAuthorizationService();
    auth.require(context, PermissionCode.IntegrityReportValidate);

    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'SUBMITTED_TO_ENGINEERING' && report.status !== 'UNDER_ENGINEERING_REVIEW') {
      throw new AppError(ErrorCode.Conflict, 'O relatório deve estar em análise pela Engenharia para ser validado.', {
        safeMessage: 'O relatório deve estar em análise pela Engenharia para ser validado.'
      });
    }

    report = await this.syncEngineerFromProject(report, context);

    let userProfessionalId: string | null = null;
    if (this.identityRepo && context.userId) {
      try {
        const user = await this.identityRepo.getUser(context.userId);
        if (user && user.professionalId) userProfessionalId = user.professionalId;
      } catch {}
    }

    const previousStatus = report.status;
    report.status = 'APPROVED';
    report.validatedAt = new Date().toISOString();
    report.validatedByUserId = context.userId || null;
    report.validatedByProfessionalId = userProfessionalId;
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.IntegrityReportValidated,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: previousStatus },
      newState: { status: 'APPROVED' },
      metadata: { reportNumber: report.reportNumber ?? null, validatedBy: context.userId ?? null }
    });

    return report;
  }

  async approveByPlh(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'SUBMITTED_TO_ENGINEERING' && report.status !== 'UNDER_ENGINEERING_REVIEW' && report.status !== 'APPROVED') {
      throw new AppError(ErrorCode.Conflict, 'Report must be under engineering to be approved');
    }

    report = await this.syncEngineerFromProject(report, context);

    let userProfessionalId: string | null = null;
    let plhUser: any = null;
    let plhProfessional: any = null;
    if (this.identityRepo && context.userId) {
      try {
        plhUser = await this.identityRepo.getUser(context.userId);
        if (plhUser && plhUser.professionalId) {
          userProfessionalId = plhUser.professionalId;
          plhProfessional = await this.identityRepo.getProfessional(plhUser.professionalId);
        }
      } catch {}
    }

    if (report.engineerPlhId && userProfessionalId && report.engineerPlhId !== userProfessionalId) {
      throw new AppError(ErrorCode.Authorization, 'Apenas o Engenheiro/PLH responsável pelo Projeto pode aprovar este relatório.');
    }

    // Real PLH qualification gate (Bloco 9) — guarded by `this.identityRepo`
    // being wired (always true in the real running app; some unit tests
    // construct ReportService without it, in which case this mirrors the
    // previous behavior rather than throwing on a dependency that was never
    // provided).
    if (this.identityRepo) {
      const { canActAsPlh } = await import("../../domain/identity/plh-eligibility.js");
      // Cliente DO RELATÓRIO, não o selecionado na sessão — mesma base já
      // usada na linha que valida o PLH responsável deste mesmo arquivo.
      // Com `context.activeClientId`, o PLH correto era recusado sempre que a
      // tela estivesse em outro Cliente (ou em nenhum), e a autorização
      // passava a depender de um valor que a interface troca livremente.
      const documentClientId = report.clientId ? String(report.clientId) : "";
      if (!documentClientId) {
        throw new AppError(ErrorCode.Validation, 'Integrity report has no client', {
          safeMessage: 'CLIENTE DO DOCUMENTO NÃO IDENTIFICADO. Não é possível autorizar a aprovação sem saber a que Cliente este relatório pertence.'
        });
      }
      const eligibility = canActAsPlh(plhUser, plhProfessional, await responsiblePlhIdOfClient(this.identityRepo as never, documentClientId));
      if (!eligibility.allowed) {
        throw new AppError(ErrorCode.Authorization, eligibility.reason || 'Usuário não habilitado como PLH.', {
          safeMessage: eligibility.reason || 'Você não está habilitado a aprovar e assinar como PLH.'
        });
      }
    }

    let hash: string | undefined;
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(report));
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      hash = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    } catch { /* hash stays undefined if Web Crypto is unavailable */ }

    report.status = 'AWAITING_SIGNATURE';
    report.approvedAt = new Date().toISOString();
    report.approvedByPlh = context.userId;
    await this.stampVisual(String(report.id));
    if (hash) report.documentHash = hash;
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Approval,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'UNDER_ENGINEERING_REVIEW' },
      newState: { status: 'AWAITING_SIGNATURE' },
      metadata: {}
    });

    if (report.inspectorId && this.identityRepo) {
      try {
        const inspector = await this.identityRepo.getProfessional(report.inspectorId);
        if (inspector && inspector.userId) {
          await notificationService.createNotification({
            userId: inspector.userId,
            type: 'SUCCESS',
            event: 'REPORT_APPROVED',
            title: 'Relatório aprovado',
            message: `O ${report.reportNumber || 'Relatório'} foi aprovado pelo PLH.`,
            entityType: 'Report',
            entityId: report.id,
            clientId: report.clientId,
            idempotencyKey: `REPORT_APPROVED_${report.id}_${report.version}`
          });
        }
      } catch (err) {
        console.warn('Falha ao gerar notificação para Inspetor', err);
      }
    }

    return report;
  }

  async signByInspector(
    context: RequestContext,
    reportId: IntegrityReportId,
    inspectorId: string
  ): Promise<IntegrityReport> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'AWAITING_SIGNATURE') {
      throw new AppError(ErrorCode.Conflict, 'Report must be AWAITING_SIGNATURE to be signed');
    }

    report.inspectorId = domainId<ProfessionalId>(inspectorId);
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Signature,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'AWAITING_SIGNATURE' },
      newState: { status: 'AWAITING_SIGNATURE', inspectorId },
      metadata: { signatory: 'INSPECTOR' }
    });

    return report;
  }

  async signByPlh(
    context: RequestContext,
    reportId: IntegrityReportId,
    plhId: string
  ): Promise<IntegrityReport> {
    let report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'AWAITING_SIGNATURE') {
      throw new AppError(ErrorCode.Conflict, 'Report must be AWAITING_SIGNATURE to be signed');
    }

    report = await this.syncEngineerFromProject(report, context);
    if (report.engineerPlhId && report.engineerPlhId !== plhId) {
      throw new AppError(ErrorCode.Authorization, 'Apenas o Engenheiro/PLH responsável pelo Projeto pode assinar este relatório.');
    }

    if (this.identityRepo) {
      const cond = report.engineeringAssessment?.equipmentCondition;
      if (!cond || !report.conclusion || !report.conclusion.trim() || report.conclusion === 'NÃO INFORMADO') {
        throw new AppError(
          ErrorCode.Validation,
          'Informe a Condição Final e a Conclusão Técnica da análise de Engenharia antes de concluir o relatório.',
          { safeMessage: 'Informe a Condição Final e a Conclusão Técnica da análise de Engenharia antes de concluir o relatório.' }
        );
      }
    }

    report.engineerPlhId = domainId<ProfessionalId>(plhId);
    report.status = 'SIGNED';
    report.updatedAt = new Date().toISOString();
    report.version++;

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.Signature,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'AWAITING_SIGNATURE' },
      newState: { status: 'SIGNED', engineerPlhId: plhId },
      metadata: { signatory: 'PLH' }
    });

    return report;
  }

  async issueReport(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<IntegrityReport> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) throw new AppError(ErrorCode.NotFound, 'Report not found');
    if (report.status !== 'SIGNED') {
      throw new AppError(ErrorCode.Conflict, 'Only signed reports can be issued');
    }

    report.status = 'COMPLETED';
    report.updatedAt = new Date().toISOString();
    report.version++;

    if (!report.verificationToken) {
      report.verificationToken = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      report.verificationCode = 'IA-' + Math.random().toString(36).substring(2, 6).toUpperCase() + '-' + Math.random().toString(36).substring(2, 6).toUpperCase();
      report.documentHash = 'SHA256-' + Date.now().toString(16);
    }

    await this.reportRepo.save(context, report);

    await this.audit?.record(context, {
      action: AuditAction.StatusChange,
      entityType: 'IntegrityReport',
      entityId: report.id,
      previousState: { status: 'SIGNED' },
      newState: { status: 'COMPLETED' },
      metadata: {}
    });

    return report;
  }

  async checkSignaturesAndConsolidate(
    context: RequestContext,
    reportId: IntegrityReportId
  ): Promise<void> {
    const report = await this.reportRepo.findById(context, reportId);
    if (!report) return;

    if (report.status === 'AWAITING_SIGNATURE') {
      report.status = 'SIGNED';
      report.updatedAt = new Date().toISOString();
      report.version++;
      await this.reportRepo.save(context, report);

      await this.audit?.record(context, {
        action: AuditAction.Signature,
        entityType: 'IntegrityReport',
        entityId: report.id,
        previousState: { status: 'AWAITING_SIGNATURE' },
        newState: { status: 'SIGNED' },
        metadata: {}
      });
    }
  }
}

export interface UpdateRecommendationFullPayload {
  version?: number;
  scope?: string;
  location?: string;
  dimension?: string;
  discipline?: string;
  criticality?: string;
  executeIn?: string;
  technicalData?: string;
  observations?: string;
  correctionSource?: string;
}
